/**
 * Netflix Home Component
 * Homepage with hero, Now Playing, Top Rated This Week, themed night strips
 */

import { useState, useEffect } from '@wordpress/element';
import HeroCarousel from './HeroCarousel';
import FilmRow from './FilmRow';
import TrailerModal from './TrailerModal';

export default function NetflixHome() {
    const [heroLoading, setHeroLoading] = useState(true);
    const [contentLoading, setContentLoading] = useState(true);
    const [error, setError] = useState(null);
    const [heroPicks, setHeroPicks] = useState([]);
    const [fallbackFilm, setFallbackFilm] = useState(null);
    const [categories, setCategories] = useState([]);
    const [trailerFilm, setTrailerFilm] = useState(null);
    const [isTrailerOpen, setIsTrailerOpen] = useState(false);

    const t = window.rialtoData?.translations || {};
    const apiBase = window.rialtoData?.apiBase || '/wp-json/rialto/v1';

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Deduplicate films by show/movie title
     */
    const deduplicateByShow = (films) => {
        const showMap = new Map();
        for (const film of films) {
            const baseTitle = getBaseShowTitle(film.title);
            if (!showMap.has(baseTitle)) {
                showMap.set(baseTitle, film);
            } else {
                const existing = showMap.get(baseTitle);
                if (shouldReplaceEntry(existing, film)) {
                    showMap.set(baseTitle, film);
                }
            }
        }
        return Array.from(showMap.values());
    };

    const getBaseShowTitle = (title) => {
        if (!title) return '';
        return title
            .replace(/:\s*(?:Series|Season)\s*\d+.*/i, '')
            .replace(/:\s*Episode\s*\d+.*/i, '')
            .replace(/\s*S\d+E\d+.*/i, '')
            .trim();
    };

    const shouldReplaceEntry = (existing, newFilm) => {
        if (!existing.poster && newFilm.poster) return true;
        if (existing.poster && !newFilm.poster) return false;
        const existingRating = parseFloat(existing.rating) || 0;
        const newRating = parseFloat(newFilm.rating) || 0;
        if (newRating > existingRating) return true;
        if (!existing.trailer_embed && newFilm.trailer_embed) return true;
        return false;
    };

    /**
     * Format 24h time to 12h (e.g. "14:30" => "2.30pm")
     */
    const formatTime12h = (time24) => {
        if (!time24) return '';
        const [h, m] = time24.split(':').map(Number);
        const period = h >= 12 ? 'pm' : 'am';
        const hour12 = h % 12 || 12;
        return `${hour12}.${String(m).padStart(2, '0')}${period}`;
    };

    /**
     * Format date to short day name (e.g. "2026-02-18" => "Wed")
     */
    const formatShortDay = (dateStr) => {
        if (!dateStr) return '';
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const d = new Date(dateStr + 'T12:00:00');
        return days[d.getDay()] || '';
    };

    /**
     * Shuffle array (Fisher-Yates) for adding variety to rows
     */
    const shuffleArray = (arr) => {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const fetchData = async () => {
        try {
            // Build feature banner slide if configured
            const fb = window.rialtoData?.featureBanner;
            let featureSlide = null;
            if (fb) {
                if (fb.source === 'film' && fb.film) {
                    // Film source: render exactly like a regular day slide but flagged as featured
                    featureSlide = {
                        is_featured: true,
                        is_film_source: true,
                        film: fb.film,
                        menu_subtitle: fb.menuSubtitle,
                        menu_title: fb.menuTitle,
                        day_name: '',
                        day_short: '',
                    };
                } else if (fb.source !== 'film') {
                    // Custom source: manual content
                    featureSlide = {
                        is_featured: true,
                        banner_image: fb.bannerImage,
                        banner_subtitle: fb.bannerSubtitle,
                        banner_title: fb.bannerTitle,
                        description: fb.description,
                        video_url: fb.videoUrl,
                        video_text: fb.videoText,
                        link_url: fb.linkUrl,
                        link_text: fb.linkText,
                        menu_subtitle: fb.menuSubtitle,
                        menu_title: fb.menuTitle,
                        day_name: '',
                        day_short: '',
                        film: null,
                    };
                }
            }

            // Fire hero fetch independently so it can render first
            const heroPromise = fetch(`${apiBase}/pick-of-day/week`).then(async (res) => {
                if (res.ok) {
                    const picksData = await res.json();
                    if (picksData?.picks?.length > 0) {
                        const picks = featureSlide
                            ? [featureSlide, ...picksData.picks]
                            : picksData.picks;
                        setHeroPicks(picks);
                    } else if (featureSlide) {
                        setHeroPicks([featureSlide]);
                    }
                } else if (featureSlide) {
                    setHeroPicks([featureSlide]);
                }
                setHeroLoading(false);
            }).catch(() => {
                if (featureSlide) {
                    setHeroPicks([featureSlide]);
                }
                setHeroLoading(false);
            });

            // Fire content fetches in parallel
            const [filmsRes, scheduleRes, scheduleWeekRes, nightsRes, nightsUpcomingRes] = await Promise.all([
                fetch(`${apiBase}/films?per_page=500`),
                fetch(`${apiBase}/schedule`),
                fetch(`${apiBase}/schedule/week`),
                fetch(`${apiBase}/nights`),
                fetch(`${apiBase}/nights/upcoming`),
            ]);

            // Wait for hero too (may already be done)
            await heroPromise;

            // Get all films
            let allFilms = [];
            if (filmsRes.ok) {
                const filmsData = await filmsRes.json();
                allFilms = filmsData.films || filmsData || [];
            }

            // Set fallback film
            if (allFilms.length > 0) {
                const withTrailer = allFilms.filter(f => f.trailer_embed || f.trailer_url);
                const withRating = withTrailer.filter(f => f.rating);
                if (withRating.length > 0) {
                    withRating.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
                    setFallbackFilm(withRating[0]);
                } else if (withTrailer.length > 0) {
                    setFallbackFilm(withTrailer[0]);
                } else {
                    setFallbackFilm(allFilms[0]);
                }
            }

            // Build category rows in the new order
            const rows = [];
            const uniqueFilms = deduplicateByShow(allFilms);

            // === 1. UP NEXT (now playing + upcoming, always aim for 6 items) ===
            let todayScheduleData = null;
            let weekScheduleData = null;
            if (scheduleRes.ok) {
                todayScheduleData = await scheduleRes.json();
            }
            if (scheduleWeekRes.ok) {
                weekScheduleData = await scheduleWeekRes.json();
            }

            if (todayScheduleData) {
                const todaySchedule = todayScheduleData.schedule || [];
                const now = new Date();
                const currentMinutes = now.getHours() * 60 + now.getMinutes();

                // Filter screenings from current time forward
                const upcoming = todaySchedule.filter(item => {
                    if (!item.time) return false;
                    const [h, m] = item.time.split(':').map(Number);
                    const screeningMinutes = h * 60 + m;
                    const endMinutes = screeningMinutes + (parseInt(item.duration) || 90);
                    return endMinutes > currentMinutes;
                });

                // Tag each film with screening time and now-playing status
                const upNextFilms = [];
                const seen = new Set();
                for (const screening of upcoming) {
                    if (!screening.film) continue;
                    const baseTitle = getBaseShowTitle(screening.film.title);
                    if (seen.has(baseTitle)) continue;
                    seen.add(baseTitle);

                    const [h, m] = screening.time.split(':').map(Number);
                    const startMin = h * 60 + m;
                    const endMin = startMin + (parseInt(screening.duration) || 90);
                    const isNowPlaying = startMin <= currentMinutes && endMin > currentMinutes;

                    upNextFilms.push({
                        ...screening.film,
                        _screeningTime: formatTime12h(screening.time),
                        _isNowPlaying: isNowPlaying,
                    });
                    if (upNextFilms.length >= 6) break;
                }

                // If we have fewer than 6, pull from tomorrow (next day in week schedule)
                if (upNextFilms.length < 6 && weekScheduleData) {
                    const weekDays = weekScheduleData.days || [];
                    // Find tomorrow's schedule (second day in the week)
                    const tomorrowDay = weekDays.length > 1 ? weekDays[1] : null;
                    if (tomorrowDay) {
                        const tomorrowScreenings = tomorrowDay.screenings || [];
                        for (const screening of tomorrowScreenings) {
                            if (upNextFilms.length >= 6) break;
                            if (!screening.film) continue;
                            const baseTitle = getBaseShowTitle(screening.film.title);
                            if (seen.has(baseTitle)) continue;
                            seen.add(baseTitle);
                            upNextFilms.push({
                                ...screening.film,
                                _screeningTime: formatTime12h(screening.time),
                                _isNowPlaying: false,
                            });
                        }
                    }
                }

                if (upNextFilms.length > 0) {
                    rows.push({
                        id: 'up-next',
                        title: 'Up Next',
                        films: upNextFilms,
                        rowType: 'upnext',
                    });
                }
            }

            // === 2. TOP RATED THIS WEEK (next 7 days, with randomisation) ===
            if (weekScheduleData) {
                const weekDays = weekScheduleData.days || weekScheduleData.schedule || [];

                // Collect all films screening this week, preserving day/time
                let weekFilms = [];
                const schedDays = Array.isArray(weekDays) ? weekDays : Object.values(weekDays);
                for (const day of schedDays) {
                    const dayScreenings = day.screenings || day.schedule || [];
                    for (const screening of dayScreenings) {
                        const film = screening.film;
                        if (film && film.rating && parseFloat(film.rating) >= 6.5) {
                            weekFilms.push({
                                ...film,
                                _screeningTime: formatTime12h(screening.time),
                                _screeningDay: formatShortDay(day.date),
                                _screeningDate: day.date,
                            });
                        }
                    }
                }

                // Deduplicate keeping first occurrence (preserves schedule data)
                const seenTitles = new Set();
                weekFilms = weekFilms.filter(f => {
                    const base = getBaseShowTitle(f.title);
                    if (seenTitles.has(base)) return false;
                    seenTitles.add(base);
                    return true;
                });
                weekFilms.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));

                if (weekFilms.length > 0) {
                    const topPool = weekFilms.slice(0, Math.min(30, weekFilms.length));
                    const shuffled = shuffleArray(topPool).slice(0, 20);
                    rows.push({
                        id: 'top-rated-week',
                        title: t.topRatedWeek || 'Top Rated This Week',
                        films: shuffled,
                        rowType: 'toprated',
                    });
                }
            }

            // If no week schedule data, fall back to overall top rated
            if (!rows.find(r => r.id === 'top-rated-week')) {
                const topRated = uniqueFilms
                    .filter(f => f.rating && parseFloat(f.rating) >= 7)
                    .sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
                if (topRated.length > 0) {
                    const shuffled = shuffleArray(topRated.slice(0, 30)).slice(0, 20);
                    rows.push({
                        id: 'top-rated',
                        title: t.topRated || 'Top Rated',
                        films: shuffled,
                    });
                }
            }

            // === 3. THEMED NIGHT STRIPS ===
            // Night slug to day/colour map
            const nightMeta = {
                british:       { day: 'Sunday',    color: '#1b74bb' },
                world:         { day: 'Monday',    color: '#f89420' },
                presentation:  { day: 'Tuesday',   color: '#3fa832' },
                directors:     { day: 'Wednesday', color: '#bfb099' },
                documentary:   { day: 'Thursday',  color: '#8c8e91' },
                film:          { day: 'Friday',    color: '#930b0b' },
                selection:     { day: 'Saturday',  color: '#92278f' },
            };

            // Get upcoming 8:30pm screenings per night
            let upcomingByNight = {};
            if (nightsUpcomingRes.ok) {
                const upcomingData = await nightsUpcomingRes.json();
                upcomingByNight = upcomingData.nights || {};
            }

            if (nightsRes.ok) {
                const nightsData = await nightsRes.json();
                const nights = nightsData.nights || nightsData || [];

                // Sort nights in weekday order (Sun-Sat)
                const slugOrder = ['british', 'world', 'presentation', 'directors', 'documentary', 'film', 'selection'];
                const sortedNights = [...nights].sort((a, b) => {
                    const aIdx = slugOrder.indexOf(a.slug);
                    const bIdx = slugOrder.indexOf(b.slug);
                    return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
                });

                for (const night of sortedNights) {
                    // Use upcoming 8:30pm screenings instead of all films in taxonomy
                    const nightFilms = (upcomingByNight[night.slug] || []).slice(0, 20);
                    const meta = nightMeta[night.slug] || {};
                    if (nightFilms.length >= 1) {
                        rows.push({
                            id: `night-${night.slug}`,
                            title: <><span className="rialto-row-title-prefix">Rialto </span>{night.name.replace(/^Rialto /, '')} &ndash; {meta.day || ''}s @ 8.30pm</>,
                            films: nightFilms,
                            link: `/nights/${night.slug}/`,
                            accentColor: meta.color || night.color || null,
                            rowType: 'night',
                        });
                    }
                }
            }

            // === 4. WITH TRAILERS ===
            // Use curated list if any films are flagged, otherwise auto
            const curatedTrailers = uniqueFilms.filter(f => f.homepage_trailers);
            const withTrailers = curatedTrailers.length > 0
                ? curatedTrailers.slice(0, 20)
                : uniqueFilms.filter(f => f.trailer_embed || f.trailer_url).slice(0, 20);
            if (withTrailers.length > 0) {
                rows.push({
                    id: 'trailers',
                    title: t.watchTrailers || 'Watch Trailers',
                    films: withTrailers,
                });
            }

            // === 5. RECENTLY ADDED ===
            // Use curated list if any films are flagged, otherwise auto
            const curatedRecent = uniqueFilms.filter(f => f.homepage_recently_added);
            const recentlyAdded = curatedRecent.length > 0
                ? curatedRecent.slice(0, 20)
                : [...uniqueFilms].sort((a, b) => b.id - a.id).slice(0, 20);
            if (recentlyAdded.length > 0) {
                rows.push({
                    id: 'recently-added',
                    title: t.recentlyAdded || 'Recently Added',
                    films: recentlyAdded,
                });
            }

            setCategories(rows);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            setError(err.message);
        } finally {
            setContentLoading(false);
        }
    };

    const handleTrailerClick = (film) => {
        setTrailerFilm(film);
        setIsTrailerOpen(true);
    };

    const handleTrailerClose = () => {
        setIsTrailerOpen(false);
        setTrailerFilm(null);
    };

    if (error && categories.length === 0) {
        return (
            <div className="rialto-netflix-error">
                <p>{t.errorLoading || 'Failed to load content. Please try again.'}</p>
                <button onClick={fetchData} className="rialto-btn rialto-btn-primary">
                    {t.retry || 'Retry'}
                </button>
            </div>
        );
    }

    return (
        <div className="rialto-netflix">
            {/* Hero Carousel - loads independently */}
            {heroLoading ? (
                <div className="rialto-hero-placeholder" />
            ) : (heroPicks.length > 0 || fallbackFilm) ? (
                <HeroCarousel
                    picks={heroPicks}
                    fallbackFilm={fallbackFilm}
                    onTrailerClick={handleTrailerClick}
                />
            ) : null}

            {/* Content Rows - show as soon as available */}
            {contentLoading ? (
                <div className="rialto-netflix-loading">
                    <div className="rialto-netflix-spinner" />
                </div>
            ) : (
                <div className="rialto-netflix-content">
                    {categories.map((category) => (
                        <FilmRow
                            key={category.id}
                            title={category.title}
                            films={category.films}
                            link={category.link}
                            onTrailerClick={handleTrailerClick}
                            accentColor={category.accentColor}
                            rowType={category.rowType}
                        />
                    ))}
                </div>
            )}

            {/* Trailer Modal */}
            <TrailerModal
                film={trailerFilm}
                isOpen={isTrailerOpen}
                onClose={handleTrailerClose}
            />
        </div>
    );
}
